<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, onMounted, reactive, watch } from "vue"
import { useI18n } from "vue-i18n"
import { useMachine } from "@xstate/vue"
import {
    createSourceAddModalMachine,
    type SourceAddModalInput,
    type SourceTypeChoice,
} from "@core/components/source/form/append/machine/source-append.machine"
import type { SourceAppendDonePayload } from "@core/components/source/form/append/source-append.types"
import { useCompanySourceLimits } from "@core/composables/useCompanySourceLimits"
import { rafraichirPlafondServi } from "@core/composables/plafond-rafraichissement"
import type { Address } from "@types"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            maxSource: "Nombre maximum de sources atteint",
            maxSourceAlready: ({
                named,
            }: {
                named: (key: string) => string
            }) => {
                const nbCurrentSources = named("nbCurrentSources")
                const nbMaxSources = named("nbMaxSources")
                return `Vous avez atteint le nombre maximum de sources de votre abonnement : ${nbCurrentSources} sur votre compte, pour un forfait de ${nbMaxSources}. Pour augmenter votre forfait, contactez notre support depuis votre espace.`
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
interface Props {
    /**
     * Quand true, la machine est (ré)initialisée avec l'input courant.
     * À passer depuis le dialog (ex. :open="visible") pour réinit à l'ouverture.
     */
    open?: boolean
    /**
     * État initial (pdl, pce, source) pour afficher directement le parcours
     */
    fromState?: SourceTypeChoice
    /**
     * Identifiant du bâtiment rattachant la source
     */
    buildingId?: string
    /**
     * Adresse du bâtiment (pour formulaire capteur)
     */
    address?: Address | null
    /**
     * Types proposés (ex. PDL + PCE uniquement sur la page compteurs). Si absent, tous les types.
     */
    allowedSourceTypes?: SourceTypeChoice[]
    /**
     * Quand false, le formulaire revient au choix du type après validation.
     */
    closeOnDone?: boolean
    /**
     * Si true : le PUT sign n’envoie pas la tâche d’onboarding côté API (ex. page onboarding ajout bâtiment + compteurs, où la tâche est lancée dans `onGoToDataCollection`).
     * @default false
     */
    consentSignDisableTask?: boolean
}

interface Emits {
    (e: "cancel"): void
    /** Fermeture après formulaire terminé ; `_id_source` si une source a été créée */
    (e: "done", _id_source?: string, source?: SourceAppendDonePayload): void
    (e: "update:state", state: string): void
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    open: true,
    fromState: undefined,
    buildingId: undefined,
    address: null,
    allowedSourceTypes: undefined,
    closeOnDone: true,
    consentSignDisableTask: false,
})

const emit = defineEmits<Emits>()

// ================================
// LIMITES ABONNEMENT
// ================================
const {
    getMaxSubscriptionSourceCount,
    getActiveSourceCount,
    getIsLimitReached,
    getPlafondServi,
    getMessagePlafond,
    getMessageRefusForfait,
    effacerRefusForfait,
} = useCompanySourceLimits()

/**
 * Message de plafond affiché en tête du formulaire :
 * 1. refus 409 `forfait_atteint` reçu à l'ajout (nombres du refus) ;
 * 2. plafond servi par l'API : sursis (non bloquant) ou blocage ;
 * 3. bloc non servi : texte historique quand la limite calculée est atteinte.
 */
const getLimitMessage = computed(
    (): {
        severite: "warn" | "error"
        titre: string
        texte: string
    } | null => {
        if (getMessageRefusForfait.value) return getMessageRefusForfait.value
        if (getPlafondServi.value) return getMessagePlafond.value
        if (!getIsLimitReached.value) return null
        return {
            severite: "error",
            titre: t("maxSource"),
            texte: t("maxSourceAlready", {
                nbMaxSources: String(getMaxSubscriptionSourceCount.value),
                nbCurrentSources: String(getActiveSourceCount.value),
            }),
        }
    }
)

/** Un refus précédent ne doit pas réapparaître à la réouverture du formulaire */
onMounted(() => effacerRefusForfait())

// ================================
// MACHINE
// ================================
const input = computed<SourceAddModalInput>(() => ({
    fromState: props.fromState,
    allowedSourceTypes: props.allowedSourceTypes,
    buildingId: props.buildingId,
    address: props.address ?? undefined,
    closeOnDone: props.closeOnDone,
}))

/**
 * Machine créée une fois à l'init avec l'input courant.
 * Le parent (SourceAppendDialog) doit utiliser une :key sur ce formulaire
 * pour remonter le composant quand open/fromState/buildingId/address changent.
 */
const machine = createSourceAddModalMachine(input.value)
const { snapshot, send } = useMachine(machine, {
    input: input.value,
})

// ================================
// DONNÉES CALCULÉES
// ================================

/**
 * État courant de la machine (nom de l'état)
 */
const getCurrentState = computed(() => {
    const v = snapshot.value.value
    return typeof v === "string" ? v : ""
})

/**
 * Indique si on affiche le sélecteur de type
 */
const getIsSourceTypeSelector = computed(
    () => getCurrentState.value === "SourceAppendFormTypeSelector"
)

/**
 * Indique si on affiche le parcours PDL
 */
const getIsSourceAppendPdl = computed(() => getCurrentState.value === "PdlForm")

/**
 * Indique si on affiche le parcours PCE
 */
const getIsSourceAppendPce = computed(() => getCurrentState.value === "PceForm")

/**
 * Indique si on affiche le formulaire capteur
 */
const getIsSensorForm = computed(() => getCurrentState.value === "SensorForm")

/**
 * Plafond atteint + ouverture directe : on affiche le sélecteur tant que l’état machine
 * correspond encore au type d’ouverture (sinon l’utilisateur a choisi un autre type au sélecteur).
 */
const getShowSelectorInsteadOfForm = computed(() => {
    if (!getIsLimitReached.value || !props.fromState) return false
    if (props.fromState === "pdl") return getIsSourceAppendPdl.value
    if (props.fromState === "pce") return getIsSourceAppendPce.value
    return !!(getIsSensorForm.value && props.buildingId)
})

/**
 * Affiche le sélecteur de type (parcours normal ou mode plafond avec désactivation ciblée)
 */
const getShouldShowTypeSelector = computed(
    () => getIsSourceTypeSelector.value || getShowSelectorInsteadOfForm.value
)

/**
 * Options du sélecteur : en plafond + ouverture directe, une seule ligne (PDL, PCE ou capteur).
 */
const getTypeSelectorAllowedTypes = computed(
    (): SourceTypeChoice[] | undefined => {
        if (getShowSelectorInsteadOfForm.value && props.fromState) {
            return [props.fromState]
        }
        return props.allowedSourceTypes
    }
)

/**
 * Sur l’étape sélecteur avec plafond atteint : tous les boutons sont désactivés
 */
const getTypeSelectorDisableAll = computed(
    () => getIsLimitReached.value && getIsSourceTypeSelector.value
)

/**
 * Ouverture directe avec plafond : seul le type demandé est désactivé
 */
const getTypeSelectorDisabledTypes = computed((): SourceTypeChoice[] => {
    if (
        !getIsLimitReached.value ||
        !getShowSelectorInsteadOfForm.value ||
        !props.fromState
    ) {
        return []
    }
    return [props.fromState]
})

/** Lien Retour sous le formulaire (masqué / désactivé selon l’étape PDL ou PCE) */
const appendShellBack = reactive({ hidden: false, disabled: false })

function onAppendShellBack(payload: {
    hidden: boolean
    disabled: boolean
}): void {
    appendShellBack.hidden = payload.hidden
    appendShellBack.disabled = payload.disabled
}

watch(getCurrentState, (state) => {
    if (state !== "PdlForm" && state !== "PceForm") {
        appendShellBack.hidden = false
        appendShellBack.disabled = false
    }
})

/**
 * Remonter l'état courant au parent (ex. pour le titre du dialog)
 */
watch(
    [getCurrentState, getIsLimitReached],
    ([state, atLimit]) => {
        emit(
            "update:state",
            atLimit ? "SourceAppendLimitReached" : String(state)
        )
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================

function onSubmitType(sourceType: SourceTypeChoice): void {
    send({ type: "SUBMIT", sourceType })
}

function onBack(): void {
    send({ type: "BACK" })
}

function onPdlDone(
    _id_source?: string,
    source?: SourceAppendDonePayload
): void {
    send({ type: "UPDATE_FORM_DONE" })
    void rafraichirPlafondServi()
    emit("done", _id_source, source)
}

function onPceDone(
    _id_source?: string,
    source?: SourceAppendDonePayload
): void {
    send({ type: "UPDATE_FORM_DONE" })
    void rafraichirPlafondServi()
    emit("done", _id_source, source)
}

function onSensorDone(
    _id_source?: string,
    source?: SourceAppendDonePayload
): void {
    send({ type: "UPDATE_FORM_DONE" })
    void rafraichirPlafondServi()
    emit("done", _id_source, source)
}

function onSensorCancel(): void {
    send({ type: "BACK" })
}
</script>

<template>
    <div class="max-w-3xl p-6 flex flex-col gap-4">
        <!-- ================================ -->
        <!-- PLAFOND SOURCES ATTEINT -->
        <!-- ================================ -->
        <BaseMessage
            v-if="getLimitMessage"
            :severity="getLimitMessage.severite"
            :closable="false"
        >
            <template #messageicon>
                <i class="pi pi-exclamation-triangle" />
            </template>
            <div class="flex flex-col gap-1">
                <strong>{{ getLimitMessage.titre }}</strong>
                <span>{{ getLimitMessage.texte }}</span>
            </div>
        </BaseMessage>

        <!-- ================================ -->
        <!-- SÉLECTEUR DE TYPE -->
        <!-- ================================ -->
        <SourceAppendFormTypeSelector
            v-if="getShouldShowTypeSelector"
            :allowed-types="getTypeSelectorAllowedTypes"
            :disable-all="getTypeSelectorDisableAll"
            :disabled-types="getTypeSelectorDisabledTypes"
            @submit="onSubmitType"
        />

        <!-- ================================ -->
        <!-- PARCOURS PDL -->
        <!-- ================================ -->
        <PdlForm
            v-else-if="getIsSourceAppendPdl && !getShowSelectorInsteadOfForm"
            :building-id="buildingId"
            :address="address ?? undefined"
            show-owner-step-back
            :append-skipped-type-selector="!!fromState"
            :consent-sign-disable-task="consentSignDisableTask"
            @back="onBack"
            @done="onPdlDone"
            @update:shell-back="onAppendShellBack"
        />

        <!-- ================================ -->
        <!-- PARCOURS PCE -->
        <!-- ================================ -->
        <PceForm
            v-else-if="getIsSourceAppendPce && !getShowSelectorInsteadOfForm"
            :building-id="buildingId"
            :address="address ?? undefined"
            show-owner-step-back
            :append-skipped-type-selector="!!fromState"
            :consent-sign-disable-task="consentSignDisableTask"
            @back="onBack"
            @done="onPceDone"
            @update:shell-back="onAppendShellBack"
        />

        <!-- ================================ -->
        <!-- FORMULAIRE CAPTEUR -->
        <!-- ================================ -->
        <SensorForm
            v-else-if="
                getIsSensorForm && buildingId && !getShowSelectorInsteadOfForm
            "
            :building-id="buildingId"
            :address="address ?? undefined"
            show-back
            :append-skipped-type-selector="!!fromState"
            @back="onBack"
            @cancel="onSensorCancel"
            @done="onSensorDone"
        />
    </div>
</template>
